// Backend base URL configuration with multiple fallback options:
// 1. Runtime config.js (public/config.js) - can be changed without rebuild
// 2. Environment variable (REACT_APP_ULSAAPP_BASE_URL) - set during build
// 3. Smart detection - uses same protocol/host if HTTPS
// 4. Development fallback - localhost:5000

let baseUrl;

// Priority 1: Runtime config (public/config.js) - can be updated without rebuild
if (typeof window !== 'undefined' && window.APP_CONFIG?.BACKEND_URL) {
  baseUrl = window.APP_CONFIG.BACKEND_URL;
  console.log('✅ Using runtime config.js BACKEND_URL:', baseUrl);
}
// Priority 2: Build-time environment variable
else if (process.env.REACT_APP_ULSAAPP_BASE_URL) {
  baseUrl = process.env.REACT_APP_ULSAAPP_BASE_URL;
  console.log('✅ Using environment variable REACT_APP_ULSAAPP_BASE_URL:', baseUrl);
}
// Priority 3: Smart detection for production HTTPS
else if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
  const hostname = window.location.hostname;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    // Production: Use the same protocol and host to avoid mixed content errors
    baseUrl = `${window.location.protocol}//${window.location.host}`;
    console.log('✅ Using smart detection (HTTPS):', baseUrl);
  } else {
    // Local HTTPS development
    baseUrl = 'http://localhost:5000';
    console.warn('⚠️ Using localhost fallback (HTTPS local):', baseUrl);
  }
}
// Priority 4: Development fallback
else {
  baseUrl = 'http://localhost:5000';
  console.warn('⚠️ Using localhost fallback (development):', baseUrl);
}

// Log the final baseUrl for debugging
if (typeof window !== 'undefined') {
  console.log('🔗 Final Backend Base URL:', baseUrl);
  console.log('📍 Current Page URL:', window.location.href);
  console.log('⚙️ Runtime Config:', window.APP_CONFIG);
}


const api = {
  img: baseUrl,
  signUp: `${baseUrl}/api/auth/signup`,
  checkEmailExists: `${baseUrl}/api/auth/check-email-exists`,

  login: `${baseUrl}/api/auth/login`,
  googleLogin: `${baseUrl}/api/auth/google-login`,
  sendOtp: `${baseUrl}/api/auth/send-otp`,
  verifyOtp: `${baseUrl}/api/auth/verify-otp`,
  refreshToken: `${baseUrl}/api/auth/refresh-token`,
  updateProfile: `${baseUrl}/api/user/updateProfile`,
  updatePassword: `${baseUrl}/api/auth/updatePassword`,
  recoverPassword: `${baseUrl}/api/auth/recover-password`,
  resetPassword: `${baseUrl}/api/auth/reset-password`,
  addCourse: `${baseUrl}/api/courses/add`,
  getCourses: `${baseUrl}/api/courses`,
  updateCourse: (id) => `${baseUrl}/api/courses/${id}`,
  getProfile: `${baseUrl}/api/user/getProfile`,
  getAllCategories: `${baseUrl}/api/categories`,
  getAllCourses: `${baseUrl}/api/courses/all`,
  getCartAndWishlist: `${baseUrl}/api/buynow`,
  addToCart: `${baseUrl}/api/buynow/cart`,
  addToWishlist: `${baseUrl}/api/buynow/wishlist`,
  removeFromWishlist: `${baseUrl}/api/user/wishlist`,
  getConversations: `${baseUrl}/api/messages/conversations`,
  buyNow: `${baseUrl}/api/buynow/buy-now`,
  addToBuyNow: `${baseUrl}/api/buynow/buy-now`,
  feedback: `${baseUrl}/api/feedback`,

  // Banner API (Public endpoint for frontend)
  getBanners: `${baseUrl}/api/banners`,

  // ✅ Instructor Criteria APIs
  getInstructorCriteria: `${baseUrl}/api/instructor-criteria`,
  submitInstructorCriteria: `${baseUrl}/api/instructor-criteria-submission/submit`,
  checkInstructorSubmissionStatus: (userId) => `${baseUrl}/api/instructor-criteria-submission/${userId}/status`,
  getAdminCriteria: `${baseUrl}/api/criteria`,
  updateAdminCriteria: `${baseUrl}/api/update-criteria`,
  submitTestVideo: `${baseUrl}/api/submit-test-video`,
  submitBlockedIssue: `${baseUrl}/api/report-blocked-issue`,
  getUserBlockedIssuesWithReplies: `${baseUrl}/api/blocked-issues/user-blocked-issues`,
  userReplyToBlockedIssue: `${baseUrl}/api/blocked-issues/user-reply`,
  submitInstitutionRequest: `${baseUrl}/api/institution-requests`,

  // User Criteria APIs
  submitUserCriteria: `${baseUrl}/api/user/submit-criteria`,
  getUserCriteriaStatus: (userId) => `${baseUrl}/api/user/criteria-status/${userId}`,

  // Dynamic endpoints (Functions)
  getCourseById: (id) => `${baseUrl}/api/courses/${id}`,
  deleteCourse: (courseId) => `${baseUrl}/api/courses/${courseId}`,
  submitRating: (courseId) => `${baseUrl}/api/courses/${courseId}/rate`,
  getUserCourseRating: (courseId, userId) => `${baseUrl}/api/courses/course/${courseId}/user/${userId}`,
  getMessages: (conversationId) => `${baseUrl}/api/messages/${conversationId}`,
  sendMessage: (conversationId) => `${baseUrl}/api/messages/${conversationId}/send`,
  publishCourse: (courseId) => `${baseUrl}/api/courses/publish/${courseId}`,
  publishCourseFromDraft: `${baseUrl}/api/courses/publish-from-draft`,
  addCoursePayment: (courseId) => `${baseUrl}/api/courses/${courseId}/payment`,
  addValue: `${baseUrl}/api/addValue`,
  instructorNotifications: `${baseUrl}/api/course-purchase-notifications`, // Correct endpoint
  instructorMarkRead: `${baseUrl}/api/instructor-notification`,
  getMyTestVideo: `${baseUrl}/api/instructor/test-video/latest`,
  getPurchasedCourses: `${baseUrl}/api/user/purchased-courses`,
  addPurchasedCourse: `${baseUrl}/api/user/add-purchased-course`,

  sendInstitutionOtp: `${baseUrl}/api/institution-requests/send-otp`,
  verifyInstitutionOtp: `${baseUrl}/api/institution-requests/verify-otp`,

  // ✅ Payment API (BHIM UPI / UPI)
  createOrder: `${baseUrl}/api/payments/create-order`,
  verifyPayment: `${baseUrl}/api/payments/verify-payment`,
  enrollFreeCourse: `${baseUrl}/api/payments/enroll-free-course`,

  getLessonComments: (lessonId) => `${baseUrl}/api/courses/lessons/${lessonId}/comments`,
  addLessonComment: (lessonId) => `${baseUrl}/api/courses/lessons/${lessonId}/comments`,
  addReplyToComment: (commentId) => `${baseUrl}/api/courses/comments/${commentId}/replies`,
  likeComment: (commentId) => `${baseUrl}/api/courses/comments/${commentId}/like`,
  dislikeComment: (commentId) => `${baseUrl}/api/courses/comments/${commentId}/dislike`,
  deleteComment: (commentId) => `${baseUrl}/api/courses/comments/${commentId}`,
  updateComment: (commentId) => `${baseUrl}/api/courses/comments/${commentId}`,
  saveLessonProgress: (courseId, lessonId) => `${baseUrl}/api/courses/${courseId}/progress/${lessonId}`,
  getCourseProgress: (courseId) => `${baseUrl}/api/courses/${courseId}/progress`,
  getAllCourseComments: (courseId) => `${baseUrl}/api/courses/${courseId}/all-comments`,

  // Test Video Notifications
  getNewTestVideoNotifications: `${baseUrl}/api/test-video-notifications`,
  markTestVideoAsRead: (id) => `${baseUrl}/api/test-video-notifications/${id}/read`,

  // Course Purchase Notifications
  getCoursePurchaseNotifications: `${baseUrl}/api/course-purchase-notifications`, // Updated endpoint
  markCoursePurchaseNotificationAsRead: (id) => `${baseUrl}/api/course-purchase-notification/${id}/read`, // Updated endpoint

  getInstructorCourses: (id) => `${baseUrl}/api/instructors/${id}/courses`,
  getInstructorDetails: `${baseUrl}/api/instructors`, // Adjust the endpoint as needed
  getInstructorDetailsWithCourses: (id) => `${baseUrl}/api/courses/instructors/${id}/details`,
  getInstructorRatings: (instructorId) => `${baseUrl}/api/user/instructor/${instructorId}/ratings`,
  getInstructorBio: (instructorId) => `${baseUrl}/api/user/instructor/${instructorId}/bio`,
  getInstructorReviews: (instructorId) => `${baseUrl}/api/user/instructor/${instructorId}/reviews`,
  getInstructorTotalWatchTime: `${baseUrl}/api/courses/instructor/total-watch-time`,
  getInstructorCourseCompletionRates: `${baseUrl}/api/courses/instructor/completion-rates`,

  // Certificate APIs
  createCertificate: `${baseUrl}/api/certificates/create`, // Endpoint to create a certificate
  getCertificatesByUser: (userId) => `${baseUrl}/api/certificates/${userId}`, // Get certificates for a specific user
  getPublicCertificate: (certificateId) => `${baseUrl}/api/certificates/publicCertificateView/${certificateId}`, // Public certificate view
  getCertificateFileUrl: (filePath) => {
    if (!filePath) return '';
    // If already a full URL, return as-is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    // Normalize path and ensure baseUrl doesn't end with /
    const normalizedPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
    const cleanBaseUrl = baseUrl?.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBaseUrl}${normalizedPath}`;
  },
  getVideoFileUrl: (filePath) => {
    if (!filePath) return '';
    // If already a full URL, return as-is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    // Normalize path and ensure baseUrl doesn't end with /
    const normalizedPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
    const cleanBaseUrl = baseUrl?.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBaseUrl}${normalizedPath}`;
  },
  updateInstructorAbout: `${baseUrl}/api/instructor-criteria-submission/update-about`,
  rateInstructor: `${baseUrl}/api/user/rate-instructor`,
  getUserNotifications: `${baseUrl}/api/user/notifications`,
  markNotificationAsRead: `${baseUrl}/api/user/notifications/:notificationId/read`,
  sendGiftEmail: `${baseUrl}/api/user/send-gift-email`,
  receiveGiftLegacy: `${baseUrl}/api/user/receive-gift`,
  addGifted: `${baseUrl}/api/user/add-gifted`,

  // Example endpoints
  getGiftsBySender: (email) => `${baseUrl}/api/user/gifts/sent?email=${email}`,
  getGiftsByRecipient: (email) => `${baseUrl}/api/user/gifts/received?email=${email}`,
  receiveGift: (giftId) => `${baseUrl}/api/user/gifts/${giftId}/receive`,
  updateGiftStatus: (id) => `${baseUrl}/api/user/gifts/${id}/status`,
  getCourseDrafts: `${baseUrl}/api/courses/drafts`,
  getCourseDraftById: (id) => `${baseUrl}/api/courses/drafts/${id}`,

  // --- DRAFT ENDPOINTS ---
  addDraftSection: (draftId) => `${baseUrl}/api/courses/drafts/${draftId}/sections`,
  editDraftSection: (draftId, sectionId) => `${baseUrl}/api/courses/drafts/${draftId}/sections/${sectionId}`,
  deleteDraftSection: (draftId, sectionId) => `${baseUrl}/api/courses/drafts/${draftId}/sections/${sectionId}`,
  addDraftLesson: (draftId, sectionId) => `${baseUrl}/api/courses/drafts/${draftId}/sections/${sectionId}/lessons`,
  editDraftLesson: (draftId, sectionId, lessonId) => `${baseUrl}/api/courses/drafts/${draftId}/sections/${sectionId}/lessons/${lessonId}`,
  deleteDraftLesson: (draftId, sectionId, lessonId) => `${baseUrl}/api/courses/drafts/${draftId}/sections/${sectionId}/lessons/${lessonId}`,
  addDraftDocument: (draftId, sectionId, lessonId) => `${baseUrl}/api/courses/drafts/${draftId}/sections/${sectionId}/lessons/${lessonId}/documents`,
  deleteDraftDocument: (draftId, sectionId, lessonId, documentId) => `${baseUrl}/api/courses/drafts/${draftId}/sections/${sectionId}/lessons/${lessonId}/documents/${documentId}`,
  updateCourseDraft: (id) => `${baseUrl}/api/courses/drafts/${id}`,
  addDraftCoursePayment: (id) => `${baseUrl}/api/courses/drafts/${id}/payment`,
  publishDraftCourse: (id) => `${baseUrl}/api/courses/drafts/${id}/publish`,
  updateCourseFromDraft: () => `${baseUrl}/api/courses/update-from-draft`,
  updateSearchHistory: `${baseUrl}/api/user/update-search-history`,

  // --- UNIVERSITY STUDENT APPLICATION ENDPOINTS ---
  submitUniStudentApplication: `${baseUrl}/api/university/criteria/submit`,
  getUniStudentApplication: (userId) => `${baseUrl}/api/university/criteria/${userId}`,
  checkUniStudentApplicationStatus: (userId) => `${baseUrl}/api/university/criteria/${userId}/check`,
  getRecommendedCourses: (userId) => `${baseUrl}/api/university/recommended-courses/${userId}`,
  getUniversityNotifications: (userId) => `${baseUrl}/api/university/notifications/${userId}`,

  // --- INSTRUCTOR PAYOUT ENDPOINTS ---
  getInstructorEarnings: `${baseUrl}/api/instructor/earnings`,
  getInstructorTransactions: `${baseUrl}/api/instructor/transactions`,
  updateInstructorPayoutMethod: `${baseUrl}/api/instructor/payout-method`,
  getInstructorPayoutHistory: `${baseUrl}/api/instructor/payout-history`,
  openai: `${baseUrl}/api/openai`, // OpenAI chat endpoint

  // --- EXAM SYSTEM ENDPOINTS ---
  getExamCategories: `${baseUrl}/api/exams/categories`,
  checkSubjectBlock: (category, subcategory, subject) => 
    `${baseUrl}/api/exams/check-subject-block?category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(subcategory)}&subject=${encodeURIComponent(subject)}`,
  createExam: `${baseUrl}/api/exams/create`,
  startExam: (examId) => `${baseUrl}/api/exams/${examId}/start`,
  saveExamProgress: (examId) => `${baseUrl}/api/exams/${examId}/save-progress`,
  submitExam: (examId) => `${baseUrl}/api/exams/${examId}/submit`,
  evaluateExam: (examId) => `${baseUrl}/api/exams/${examId}/evaluate`,
  getExamById: (examId) => `${baseUrl}/api/exams/${examId}`,
  getExamHistory: `${baseUrl}/api/exams/history`,

  // --- SUBJECT SELECTION ---
  createSubjectSelection: `${baseUrl}/api/subject-selections`,
  getUserSubjectSelection: `${baseUrl}/api/subject-selections`,

  // --- AI PROCTORING ENDPOINTS ---
  baseURL: baseUrl, // For use in AIProctorService
  initializeAISession: `${baseUrl}/api/ai-proctor/session/initialize`,
  analyzeVideo: `${baseUrl}/api/ai-proctor/analyze/video`,
  analyzeAudio: `${baseUrl}/api/ai-proctor/analyze/audio`,
  analyzeKeystroke: `${baseUrl}/api/ai-proctor/analyze/keystroke`,
  reportTabSwitch: `${baseUrl}/api/ai-proctor/event/tab-switch`,
  reportSecurityViolation: `${baseUrl}/api/ai-proctor/event/security-violation`,
  getSessionStatus: (sessionId) => `${baseUrl}/api/ai-proctor/session/${sessionId}/status`,
  updateSessionConfig: (sessionId) => `${baseUrl}/api/ai-proctor/session/${sessionId}/config`,
  startAIMonitoring: (sessionId) => `${baseUrl}/api/ai-proctor/session/${sessionId}/start`,
  finalizeAISession: (sessionId) => `${baseUrl}/api/ai-proctor/session/${sessionId}/finalize`,
  getSessionAudit: (sessionId) => `${baseUrl}/api/ai-proctor/session/${sessionId}/audit`,
  getCheatingEvents: (sessionId) => `${baseUrl}/api/ai-proctor/session/${sessionId}/events`,

  // Policy Content APIs
  getPolicyContent: (type) => `${baseUrl}/api/admin/policies/${type}`, // 'privacy-policy' or 'terms-conditions'

  // FAQ APIs
  getFAQs: `${baseUrl}/api/faqs`, // Get all active FAQs
  getFAQById: (id) => `${baseUrl}/api/faqs/${id}`, // Get single FAQ by ID
  createFAQ: `${baseUrl}/api/faqs`, // Create new FAQ (Admin)
  updateFAQ: (id) => `${baseUrl}/api/faqs/${id}`, // Update FAQ (Admin)
  deleteFAQ: (id) => `${baseUrl}/api/faqs/${id}`, // Delete FAQ (Admin)
  getAllFAQsAdmin: `${baseUrl}/api/faqs/admin/all`, // Get all FAQs including inactive (Admin)
  bulkCreateFAQs: `${baseUrl}/api/faqs/bulk-create`, // Bulk create FAQs (Admin)
};

export default api;
